/**
 * Row 43 (brief 2026-10-11-001): never a blank page for a refused user.
 *
 * Every refusal in ProtectedRoute (and Layout's no-workspace safety net) sends the user to
 * getDefaultRoute(). When that default IS the page that refused them, <Navigate> to the same
 * path renders nothing — a blank screen. RefuseTo shows NoAccessView instead in that one case;
 * every other refusal still redirects as before. Who may open a page is unchanged.
 */
import { useContext } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageContext } from '../../contexts/LanguageContext';
import { usePermissions } from '../../hooks/usePermissions';
import { firstOpenPage } from './noAccessLandings';

// Lao drafts — flagged for native review in the brief's report.
const TEXT = {
  lo: {
    title: 'ບໍ່ມີສິດເຂົ້າເຖິງໜ້ານີ້',
    body: 'ບັນຊີຂອງທ່ານບໍ່ມີສິດເປີດໜ້ານີ້. ຖ້າທ່ານຄິດວ່ານີ້ບໍ່ຖືກຕ້ອງ, ກະລຸນາຕິດຕໍ່ຜູ້ດູແລລະບົບ.',
    go: 'ໄປທີ່ໜ້າທີ່ທ່ານເປີດໄດ້',
    logout: 'ອອກຈາກລະບົບ',
  },
  en: {
    title: 'No access to this page',
    body: 'Your account cannot open this page. If you think this is wrong, please contact your administrator.',
    go: 'Go to a page you can open',
    logout: 'Log out',
  },
};

export function NoAccessView() {
  // Provider-optional (as utils/ratingLabels): Lao when rendered outside the LanguageProvider.
  const language = useContext(LanguageContext)?.language ?? 'lo';
  const t = TEXT[language];
  const { logout } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const location = useLocation();
  const target = firstOpenPage(hasAnyPermission, location.pathname);

  return (
    <div data-testid="no-access-view" className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t.title}</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">{t.body}</p>
        <div className="mt-6">
          {target ? (
            <Link
              to={target}
              replace
              className="inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t.go}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => logout?.()}
              className="rounded-md bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              {t.logout}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A refusal: redirect to the user's default page, unless that is this page (→ NoAccessView). */
export function RefuseTo() {
  const { getDefaultRoute } = useAuth();
  const location = useLocation();
  const target = getDefaultRoute();
  // Compare the path only: a default may carry a query (e.g. /fleet/plans?mode=today).
  if (target.split('?')[0] === location.pathname) return <NoAccessView />;
  return <Navigate to={target} replace />;
}
